import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Virtuoso } from 'react-virtuoso';
import { FadeContainer } from '@components/basics/Animation/FadeContainer';
import { UserRowSkeleton } from '@components/modals/shared/UserList/UserRow';
import { StyledScrollArea } from '../shared';

type FollowListProps<T> = {
  data: T[];
  loading: boolean;
  emptyComponent: JSX.Element;
  loadMore: () => void;
  onAtTopChange: (atTop: boolean) => void;
  onAtBottomChange: (atBottom: boolean) => void;
  renderItem: (i: number, user: T) => JSX.Element;
};

const Header = () => <div className="h-[18px]" />;

const DEFAUL_ITEM_HEIGHT = 52;
const TOP_THRESHOLD = 18;
const BOTTOM_THRESHOLD = 26;

export function List<T>({
  data,
  loading,
  emptyComponent,
  loadMore,
  onAtTopChange,
  onAtBottomChange,
  renderItem,
}: FollowListProps<T>) {
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | undefined>();
  const isEmpty = !!!data?.length;

  const emptyList = useMemo(
    () => (
      <div className={clsx('h-6 px-6', !data.length && 'mt-[18px]')}>
        <UserRowSkeleton />
        <UserRowSkeleton />
        <UserRowSkeleton />
      </div>
    ),
    [data.length],
  );

  const LoadingFooter = useCallback(
    () => <FadeContainer>{loading ? emptyList : <div className="h-[18px]" />}</FadeContainer>,
    [emptyList, loading],
  );

  if (isEmpty && loading) {
    return (
      <FadeContainer>
        <StyledScrollArea>{emptyList}</StyledScrollArea>
      </FadeContainer>
    );
  }

  if (isEmpty) {
    return (
      <FadeContainer>
        <StyledScrollArea>{emptyComponent}</StyledScrollArea>
      </FadeContainer>
    );
  }

  return (
    <FadeContainer>
      <StyledScrollArea
        ref={r => {
          if (r) {
            setScrollParent(r);
          }
        }}
        empty={isEmpty}
      >
        <Virtuoso
          data={data}
          itemContent={renderItem}
          defaultItemHeight={DEFAUL_ITEM_HEIGHT}
          atTopThreshold={TOP_THRESHOLD}
          atBottomThreshold={BOTTOM_THRESHOLD}
          customScrollParent={scrollParent}
          endReached={loadMore}
          components={{ Header, Footer: LoadingFooter }}
          atTopStateChange={atTop => onAtTopChange(atTop)}
          atBottomStateChange={atBottom => onAtBottomChange(atBottom)}
        />
      </StyledScrollArea>
    </FadeContainer>
  );
}
